import { useEffect, useState } from 'react';
import { db, auth } from '../FirebaseAuthConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

import Loader from '../Pages/loader.js';

function ProfileBlock() {
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    const [userData, setUserData] = useState({});
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [participationType, setParticipationType] = useState('');
    const [battlePartner, setBattlePartner] = useState('');
    const [teamName, setTeamName] = useState('');

    useEffect(() => {
        if (!auth.currentUser) {
            return;
        }

        const getUserData = async () => {
            const userRef = doc(db, `userCollection/${auth.currentUser.uid}`);
            const userSnap = await getDoc(userRef);
            const userDataResult = userSnap.data() || {};
            setUserData(userDataResult);

            setDisplayName(userDataResult.displayName || '');
            setEmail(userDataResult.email || '');
            setPhone(userDataResult.phone || '');

            const challengeRef = doc(db, `userCollection/${auth.currentUser.uid}/challengeData/everydayWarship`);
            const challengeSnap = await getDoc(challengeRef);
            const challengeData = challengeSnap.data() || {};

            setParticipationType(challengeData.participationType || '');
            setBattlePartner(challengeData.battlePartner || '');

            // If user has a team (battlePartner mode), get team name
            if (challengeData.participationType === 'battlePartner' && challengeData.team) {
                const teamRef = doc(db, `challengeCollection/everydayWarship/teams/${challengeData.team}`);
                const teamSnap = await getDoc(teamRef);
                if (teamSnap.exists()) {
                    setTeamName(teamSnap.data().name || '');
                }
            }

            setIsLoading(false);
        };

        getUserData();
    }, []);

    const submitData = async (e) => {
        e.preventDefault();
        setIsSaving(true);

        try {
            const userRef = doc(db, `userCollection/${auth.currentUser.uid}`);
            await updateDoc(userRef, {
                displayName: displayName,
                email: email,
                phone: phone,
            });

            window.location.reload();
        } catch (error) {
            console.error('Error updating profile:', error);
            setIsSaving(false);
        }
    };

    // Get display text for participation type
    const getParticipationDisplay = () => {
        switch (participationType) {
            case 'battlePartner':
                return 'Battle Partner';
            case 'individual':
                return 'Individual';
            default:
                return participationType || 'Not set';
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="w-full flex flex-col gap-4">
            {/* Profile Card */}
            <div className="w-full rounded-2xl bg-neutral-200 dark:bg-neutral-700 p-6">
                <h2 className="font-sans text-xl font-bold text-neutral-800 dark:text-white mb-6">
                    User Profile
                </h2>

                <form className="flex flex-col gap-4" onSubmit={submitData}>
                    {/* Name Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-300">
                                First Name
                            </label>
                            <input
                                className="w-full h-12 px-4 border-none rounded-xl font-sans text-base font-medium text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed"
                                type="text"
                                disabled
                                value={userData.firstName || ''}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-300">
                                Last Name
                            </label>
                            <input
                                className="w-full h-12 px-4 border-none rounded-xl font-sans text-base font-medium text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed"
                                type="text"
                                disabled
                                value={userData.lastName || ''}
                            />
                        </div>
                    </div>

                    {/* Display Name */}
                    <div className="flex flex-col gap-1">
                        <label className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-300">
                            Display Name
                        </label>
                        <input
                            className="w-full h-12 px-4 border-none rounded-xl font-sans text-base font-medium text-neutral-800 dark:text-white bg-white dark:bg-neutral-800 focus:outline focus:outline-2 focus:outline-[#FF671F]"
                            type="text"
                            value={displayName}
                            required
                            onChange={(e) => setDisplayName(e.target.value)}
                        />
                    </div>

                    {/* Contact Info Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-300">
                                Email Address
                            </label>
                            <input
                                className={`w-full h-12 px-4 border-none rounded-xl font-sans text-base font-medium ${
                                    userData.authWithGoogle === 'true'
                                        ? 'text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed'
                                        : 'text-neutral-800 dark:text-white bg-white dark:bg-neutral-800 focus:outline focus:outline-2 focus:outline-[#FF671F]'
                                }`}
                                type="email"
                                disabled={userData.authWithGoogle === 'true'}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-300">
                                Phone Number
                            </label>
                            <input
                                className={`w-full h-12 px-4 border-none rounded-xl font-sans text-base font-medium ${
                                    userData.authWithGoogle === 'false'
                                        ? 'text-neutral-400 dark:text-neutral-500 bg-neutral-100 dark:bg-neutral-600 cursor-not-allowed'
                                        : 'text-neutral-800 dark:text-white bg-white dark:bg-neutral-800 focus:outline focus:outline-2 focus:outline-[#FF671F]'
                                }`}
                                type="tel"
                                disabled={userData.authWithGoogle === 'false'}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Save Button */}
                    <button
                        className="w-full md:w-full md:self-end h-12 px-8 mt-2 border-none rounded-xl font-sans text-base font-semibold text-white bg-[#FF671F] hover:bg-[#e55a1a] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        type="submit"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </form>
            </div>

            {/* Challenge Info Card */}
            <div className="w-full rounded-2xl bg-neutral-200 dark:bg-neutral-700 p-6">
                <h2 className="font-sans text-xl font-bold text-neutral-800 dark:text-white mb-4">
                    Challenge Info
                </h2>

                <div className="flex flex-col gap-3">
                    {/* Participation Type */}
                    <div className="flex flex-row justify-between items-center py-2 border-b border-neutral-300 dark:border-neutral-600">
                        <span className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-400">
                            Participation Type
                        </span>
                        <span className="font-sans text-sm font-semibold text-neutral-800 dark:text-white">
                            {getParticipationDisplay()}
                        </span>
                    </div>

                    {/* Team Name (if applicable) */}
                    {participationType === 'battlePartner' && teamName && (
                        <div className="flex flex-row justify-between items-center py-2 border-b border-neutral-300 dark:border-neutral-600">
                            <span className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-400">
                                Team Name
                            </span>
                            <span className="font-sans text-sm font-semibold text-neutral-800 dark:text-white">
                                {teamName}
                            </span>
                        </div>
                    )}

                    {/* Battle Partner (if applicable) */}
                    {participationType === 'battlePartner' && battlePartner && (
                        <div className="flex flex-row justify-between items-center py-2 border-b border-neutral-300 dark:border-neutral-600">
                            <span className="font-sans text-sm font-medium text-neutral-600 dark:text-neutral-400">
                                Battle Partner Contact
                            </span>
                            <span className="font-sans text-sm font-semibold text-neutral-800 dark:text-white">
                                {battlePartner}
                            </span>
                        </div>
                    )}

                    {/* Support Link */}
                    <p className="font-sans text-xs text-neutral-500 dark:text-neutral-400 mt-2">
                        Need to change your participation type or team?{' '}
                        <a 
                            href="https://staff.elevationchurch.org/support/tickets/new?category=Software&sub_category=Account%20Access&subject=Everyday%20WAR-Ship%20-%20Profile%20Change%20Request"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#FF671F] hover:underline"
                        >
                            Submit a support ticket
                        </a>
                    </p>
                </div>
            </div>

            {/* Sign Out Button */}
            <button
                className="w-full h-12 border-none rounded-xl font-sans text-base font-semibold text-neutral-600 dark:text-neutral-300 bg-neutral-300 dark:bg-neutral-600 hover:bg-neutral-400 dark:hover:bg-neutral-500 transition-colors cursor-pointer"
                onClick={() => {
                    auth.signOut();
                    window.location.replace('/auth');
                }}
            >
                Sign Out
            </button>
        </div>
    );
}

export default ProfileBlock;
